import React, { createRef } from "react";
import styled, { keyframes } from "styled-components";

const SIGNATURE_IFRAME_URL_KEY = "applyFrame_signatureIframeUrl";

export type QuestionPageChangeEvent = {
  newQuestionPage: string;
};

interface Props {
  redirectUri?: string;
  applyUri: string;
  formId: string;
  questionPage?: string;
  minHeight?: number;
  onQuestionPageChange?: (event: QuestionPageChangeEvent) => void;
  onFinishEvent?: () => void;
}

interface State {
  isLoading: boolean;
}

class ApplyFrame extends React.Component<Props, State> {
  private iframeRef = createRef<HTMLIFrameElement>();
  private lastIframeUrl: string | null = null;
  private restoredIframeUrl: string | null = null;

  constructor(props: Props) {
    super(props);

    this.state = { isLoading: true };

    const saved = sessionStorage.getItem(SIGNATURE_IFRAME_URL_KEY);
    if (saved) {
      const isSignatureReturn = new URLSearchParams(window.location.search).has("CurrentQuestionPageId");
      if (isSignatureReturn) {
        this.restoredIframeUrl = saved;
      }
      sessionStorage.removeItem(SIGNATURE_IFRAME_URL_KEY);
    }

    window.addEventListener("message", this.msgHandler);
  }

  msgHandler = (e: any) => {
    if (e.data === "unchanged") {
      return;
    }

    if (e.data.type === "signatureRedirectUrl") {
      if (this.lastIframeUrl) {
        sessionStorage.setItem(SIGNATURE_IFRAME_URL_KEY, this.lastIframeUrl);
      }
      const msg = {
        type: "signatureRedirectUrl",
        url: window.location.href
      };
      if (this.iframeRef.current?.contentWindow) {
        this.iframeRef.current.contentWindow.postMessage(msg, "*");
      }
    }

    if (e.data.type === "heightUpdate") {
      let newHeight = e.data.newHeight;

      if (this.props.minHeight && this.props.minHeight > newHeight) {
        newHeight = this.props.minHeight;
      }
      if (this.iframeRef && this.iframeRef.current) {
        this.iframeRef.current.height = `${newHeight}px`;
      }
    }

    if (e.data.type === "finishEvent") {
      this.props.onFinishEvent();
    }

    if (e.data.type === "pageChange") {
      const newPage = e.data.newPage !== "FirstPage" ? e.data.newPage : "";
      const newUrl = this.UpdateQueryString("CurrentQuestionPageId", newPage, window.location.href);
      window.history.pushState({ page: e.data.newPage, newUrl: e.data.newUrl }, "", newUrl);
      this.iframeRef.current.contentWindow.location.replace(e.data.newUrl);
      this.lastIframeUrl = e.data.newUrl;
      sessionStorage.removeItem(SIGNATURE_IFRAME_URL_KEY);
    }
  };

  shouldComponentUpdate(_: Props, nextState: State) {
    return this.state.isLoading !== nextState.isLoading;
  }

  onLoad = () => {
    this.setState({ isLoading: false });
    parent.scroll(0, 0);
  };

  UpdateQueryString(key: string, value: string, url: string) {
    if (!url) url = window.location.href;
    const re = new RegExp("([?&])" + key + "=.*?(&|#|$)(.*)", "gi");

    if (re.test(url)) {
      if (typeof value !== "undefined" && value !== null) {
        return url.replace(re, "$1" + key + "=" + value + "$2$3");
      } else {
        const hash = url.split("#");
        url = hash[0].replace(re, "$1$3").replace(/(&|\?)$/, "");
        if (typeof hash[1] !== "undefined" && hash[1] !== null) {
          url += "#" + hash[1];
        }
        return url;
      }
    } else {
      if (typeof value !== "undefined" && value !== null) {
        const separator = url.indexOf("?") !== -1 ? "&" : "?";
        const hash = url.split("#");
        url = hash[0] + separator + key + "=" + value;
        if (typeof hash[1] !== "undefined" && hash[1] !== null) {
          url += "#" + hash[1];
        }
        return url;
      } else {
        return url;
      }
    }
  }

  render() {
    let url: string;

    if (this.restoredIframeUrl) {
      url = this.restoredIframeUrl;
    } else {
      url = `${this.props.applyUri}/Form/${this.props.formId}?isEmbedded=True`;

      if (this.props.redirectUri) {
        url = this.props.redirectUri;
      }

      if (this.props.questionPage) {
        url += `&CurrentQuestionPageId=${this.props.questionPage}`;
      }
    }
    return (
      <Container>
        {this.state.isLoading && (
          <LoaderOverlay>
            <Spinner />
          </LoaderOverlay>
        )}

        <StyledIframe
          title="test"
          src={url}
          frameBorder={0}
          height={this.props.minHeight}
          width="100%"
          onLoad={this.onLoad}
          scrolling="no"
          ref={this.iframeRef}
          isLoading={this.state.isLoading}
        />
      </Container>
    );
  }
}

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const LoaderOverlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #fafafa;
  z-index: 2;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  border: 10px solid #f3f3f3;
  border-top: 10px solid var(--primary-color);
  border-radius: 50%;
  width: 100px;
  height: 100px;
  animation: ${spin} 1s linear infinite;
`;

const StyledIframe = styled.iframe<{ isLoading: boolean }>`
  opacity: ${({ isLoading }) => (isLoading ? 0 : 1)};
  transition: opacity 0.3s ease-in-out;
  width: 100%;
  border: none;
`;

export default ApplyFrame;
